import { useCallback, useEffect, useMemo } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import {
    CalendarOutlined,
    GiftOutlined,
    PlusOutlined,
    RiseOutlined,
    SaveOutlined,
    ScheduleOutlined,
    SettingOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { App, Button, DatePicker, Input, InputNumber, Select, Space, Spin, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import Permission, { useAccess } from '@/components/permission';
import {
    ACTIVITY_1011_CHECKIN_DAYS,
    ACTIVITY_1011_CONFIG_KEY,
    ACTIVITY_1011_CONFIG_MODULE,
    ACTIVITY_1011_METRIC_KEYS,
    ACTIVITY_1011_SHARE_PLATFORMS,
    ACTIVITY_1011_TASK_CONFIG_PERMISSION,
    ACTIVITY_1011_TASK_CONFIG_UPDATE_PERMISSION,
    ACTIVITY_1011_TASK_TYPE_LABELS,
} from '@/constants/activity-1011';
import { useActivity1011Config } from '@/pages/activity-1011/task-config/hooks/useActivity1011Config';
import {
    createRewardRankTier,
    createTaskItem,
    defaultCheckinDailyPoints,
    ensurePlatformMetrics,
    getNextTaskId,
    getTaskTypeLabel,
} from '@/pages/activity-1011/task-config/utils';

import type {
    Activity1011ConfigSection,
    Activity1011RewardRankTier,
    Activity1011TaskItem,
} from '@/types/activity-1011';
import type { ProColumns } from '@ant-design/pro-components';
import type { ReactNode } from 'react';

import './index.less';

const CONFIG_PERMISSION = ACTIVITY_1011_TASK_CONFIG_PERMISSION;
const CONFIG_UPDATE_PERMISSION = ACTIVITY_1011_TASK_CONFIG_UPDATE_PERMISSION;

type TimeRange = [Dayjs | null, Dayjs | null] | null;
type TaskRow = Activity1011TaskItem & { rowKey: string };
type RewardRow = Activity1011RewardRankTier & { rowKey: string };

const METRIC_LABEL_IDS: Record<string, string> = {
    play: 'activity1011Config.metrics.play',
    like: 'activity1011Config.metrics.like',
    retweet: 'activity1011Config.metrics.retweet',
};

const PLATFORM_LABEL_IDS: Record<string, string> = {
    x: 'activity1011Config.platforms.x',
    tiktok: 'activity1011Config.platforms.tiktok',
    youtube: 'activity1011Config.platforms.youtube',
};

const SectionTitle = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
    <span className="section-title">
        <span className="section-icon">{icon}</span>
        <span className="section-title__text">{children}</span>
    </span>
);

const PageActivity1011TaskConfig: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();
    const { loading, savingSection, configRecord, config, setConfig, loadConfig, saveSection } =
        useActivity1011Config();

    const canEdit = access(CONFIG_UPDATE_PERMISSION);
    const readOnly = !canEdit;

    useEffect(() => {
        void loadConfig().catch((error: any) => {
            message.error(error?.message || formatMessage({ id: 'activity1011Config.load.failed' }));
        });
    }, [formatMessage, loadConfig, message]);

    const timeRange: TimeRange = useMemo(() => {
        const start = config.activityStartAt ? dayjs(config.activityStartAt) : null;
        const end = config.activityEndAt ? dayjs(config.activityEndAt) : null;
        if (!start?.isValid() && !end?.isValid()) return null;
        return [start?.isValid() ? start : null, end?.isValid() ? end : null];
    }, [config.activityEndAt, config.activityStartAt]);

    const platforms = useMemo(
        () => (config.sharePlatforms?.length ? config.sharePlatforms : [...ACTIVITY_1011_SHARE_PLATFORMS]),
        [config.sharePlatforms],
    );

    const metricRules = useMemo(
        () => ensurePlatformMetrics(config.shareMetricsPointsRules || {}, platforms),
        [config.shareMetricsPointsRules, platforms],
    );

    const checkinPoints = useMemo(
        () =>
            config.checkinDailyPoints?.length
                ? config.checkinDailyPoints
                : defaultCheckinDailyPoints(),
        [config.checkinDailyPoints],
    );

    const taskTypeOptions = useMemo(
        () =>
            Object.keys(ACTIVITY_1011_TASK_TYPE_LABELS).map((type) => ({
                value: type,
                label: `${ACTIVITY_1011_TASK_TYPE_LABELS[type]} (${type})`,
            })),
        [],
    );

    const handleSaveSection = useCallback(
        async (section: Activity1011ConfigSection) => {
            try {
                const result = await saveSection(section, config);
                if (result.conflict) {
                    message.warning(formatMessage({ id: 'activity1011Config.save.conflict' }));
                    await loadConfig();
                    return;
                }
                message.success(formatMessage({ id: 'activity1011Config.save.success' }));
            } catch (error: any) {
                message.error(error?.message || formatMessage({ id: 'activity1011Config.save.failed' }));
            }
        },
        [config, formatMessage, loadConfig, message, saveSection],
    );

    const handleReload = useCallback(() => {
        void loadConfig().catch((error: any) =>
            message.error(error?.message || formatMessage({ id: 'activity1011Config.load.failed' })),
        );
    }, [formatMessage, loadConfig, message]);

    const sectionSaveExtra = (section: Activity1011ConfigSection) =>
        readOnly ? null : (
            <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingSection === section}
                disabled={!!savingSection && savingSection !== section}
                onClick={() => void handleSaveSection(section)}
            >
                <FormattedMessage id="activity1011Config.saveSection" />
            </Button>
        );

    const taskColumns = useMemo<ProColumns<TaskRow>[]>(
        () => [
            {
                title: <FormattedMessage id="activity1011Config.columns.taskId" />,
                dataIndex: 'taskId',
                width: 100,
                editable: false,
                render: (_, record) =>
                    record.taskId === '' || record.taskId == null ? (
                        <Typography.Text type="secondary">-</Typography.Text>
                    ) : (
                        record.taskId
                    ),
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.type" />,
                dataIndex: 'type',
                width: 160,
                renderFormItem: (_, { record }) => {
                    const current = record?.type;
                    const options =
                        current && !taskTypeOptions.some((item) => item.value === current)
                            ? [{ value: current, label: `${getTaskTypeLabel(current)} (${current})` }, ...taskTypeOptions]
                            : taskTypeOptions;
                    return (
                        <Select
                            showSearch
                            allowClear
                            options={options}
                            placeholder={formatMessage({ id: 'activity1011Config.fields.typePlaceholder' })}
                            optionFilterProp="label"
                        />
                    );
                },
                render: (_, record) =>
                    record.type ? (
                        <Tag color="cyan">{getTaskTypeLabel(record.type)}</Tag>
                    ) : (
                        <Typography.Text type="secondary">-</Typography.Text>
                    ),
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.points" />,
                dataIndex: 'points',
                width: 100,
                valueType: 'digit',
                fieldProps: { min: 0 },
                render: (_, record) => <Typography.Text strong>{record.points ?? 0}</Typography.Text>,
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.linkUrl" />,
                dataIndex: 'linkUrl',
                width: 280,
                ellipsis: true,
                render: (_, record) => {
                    if (!record.linkUrl) {
                        return <Typography.Text type="secondary">-</Typography.Text>;
                    }
                    const isExternal = /^https?:\/\//i.test(record.linkUrl);
                    return isExternal ? (
                        <Typography.Link href={record.linkUrl} target="_blank" rel="noreferrer">
                            {record.linkUrl}
                        </Typography.Link>
                    ) : (
                        <Typography.Text>{record.linkUrl}</Typography.Text>
                    );
                },
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.action" />,
                valueType: 'option',
                width: 120,
                render: (_, record, index, action) =>
                    readOnly
                        ? '-'
                        : [
                              <a key="edit" onClick={() => action?.startEditable?.(record.rowKey)}>
                                  <FormattedMessage id="b.edit" />
                              </a>,
                              <a
                                  key="delete"
                                  onClick={() => {
                                      setConfig((prev) => ({
                                          ...prev,
                                          tasks: prev.tasks.filter((_, i) => i !== index),
                                      }));
                                  }}
                              >
                                  <FormattedMessage id="b.delete" />
                              </a>,
                          ],
            },
        ],
        [formatMessage, readOnly, setConfig, taskTypeOptions],
    );

    const rewardColumns = useMemo<ProColumns<RewardRow>[]>(
        () => [
            {
                title: <FormattedMessage id="activity1011Config.columns.minRank" />,
                dataIndex: 'minRank',
                width: 120,
                valueType: 'digit',
                fieldProps: { min: 1, precision: 0 },
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.maxRank" />,
                dataIndex: 'maxRank',
                width: 120,
                valueType: 'digit',
                fieldProps: { min: 1, precision: 0 },
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.amount" />,
                dataIndex: 'amount',
                width: 140,
                valueType: 'digit',
                fieldProps: { min: 0, precision: 2, step: 0.01 },
                render: (_, record) => (
                    <Typography.Text strong className="amount-value">
                        {Number(record.amount ?? 0).toFixed(2)}
                    </Typography.Text>
                ),
            },
            {
                title: <FormattedMessage id="activity1011Config.columns.action" />,
                valueType: 'option',
                width: 120,
                render: (_, record, index, action) =>
                    readOnly
                        ? '-'
                        : [
                              <a key="edit" onClick={() => action?.startEditable?.(record.rowKey)}>
                                  <FormattedMessage id="b.edit" />
                              </a>,
                              <a
                                  key="delete"
                                  onClick={() => {
                                      setConfig((prev) => ({
                                          ...prev,
                                          rewardRankTiers: prev.rewardRankTiers.filter((_, i) => i !== index),
                                      }));
                                  }}
                              >
                                  <FormattedMessage id="b.delete" />
                              </a>,
                          ],
            },
        ],
        [readOnly, setConfig],
    );

    const taskDataSource = useMemo<TaskRow[]>(
        () =>
            (config.tasks || []).map((task, index) => ({
                ...task,
                rowKey: `task-${task.taskId || index}-${index}`,
            })),
        [config.tasks],
    );

    const rewardDataSource = useMemo<RewardRow[]>(
        () =>
            (config.rewardRankTiers || []).map((tier, index) => ({
                ...tier,
                rowKey: `reward-${tier.minRank}-${tier.maxRank}-${index}`,
            })),
        [config.rewardRankTiers],
    );

    return (
        <Permission permission={CONFIG_PERMISSION}>
            <Spin spinning={loading}>
                <div className="activity1011-task-config">
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <div className="page-hero">
                            <div>
                                <Typography.Title level={4} className="page-hero__title">
                                    <FormattedMessage id="activity1011Config.title" />
                                </Typography.Title>
                                <div className="page-hero__desc">
                                    <FormattedMessage id="activity1011Config.pageDesc" />
                                </div>
                                <div className="page-hero__meta">
                                    <span className="meta-pill">key: {ACTIVITY_1011_CONFIG_KEY}</span>
                                    <span className="meta-pill">module: {ACTIVITY_1011_CONFIG_MODULE}</span>
                                    <span
                                        className={`meta-pill ${configRecord?.id ? 'meta-pill--ok' : 'meta-pill--new'}`}
                                    >
                                        {configRecord?.id
                                            ? formatMessage({ id: 'activity1011Config.configExists' })
                                            : formatMessage({ id: 'activity1011Config.configNew' })}
                                    </span>
                                </div>
                            </div>
                            <Space wrap>
                                <Button onClick={handleReload}>
                                    <FormattedMessage id="activity1011Config.reload" />
                                </Button>
                            </Space>
                        </div>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<SettingOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.basic" />
                                </SectionTitle>
                            }
                            extra={sectionSaveExtra('basic')}
                        >
                            <div className="basic-grid">
                                <div className="field-block">
                                    <span className="field-block__label">
                                        <FormattedMessage id="activity1011Config.fields.title" />
                                    </span>
                                    <Input
                                        size="large"
                                        disabled={readOnly}
                                        value={config.title}
                                        onChange={(e) =>
                                            setConfig((prev) => ({ ...prev, title: e.target.value }))
                                        }
                                    />
                                </div>
                                <div className="field-block">
                                    <span className="field-block__label">
                                        <FormattedMessage id="activity1011Config.fields.activityId" />
                                    </span>
                                    <InputNumber
                                        size="large"
                                        style={{ width: '100%' }}
                                        disabled={readOnly}
                                        min={1}
                                        precision={0}
                                        value={
                                            config.activityId == null || config.activityId === ''
                                                ? undefined
                                                : Number(config.activityId)
                                        }
                                        onChange={(value) =>
                                            setConfig((prev) => ({
                                                ...prev,
                                                activityId: value == null ? '' : value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        </ProCard>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<ScheduleOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.time" />
                                </SectionTitle>
                            }
                            extra={sectionSaveExtra('time')}
                        >
                            <DatePicker.RangePicker
                                showTime
                                size="large"
                                style={{ width: '100%', maxWidth: 520 }}
                                value={timeRange as any}
                                disabled={readOnly}
                                onChange={(range) => {
                                    setConfig((prev) => ({
                                        ...prev,
                                        activityStartAt: range?.[0]?.valueOf(),
                                        activityEndAt: range?.[1]?.valueOf(),
                                    }));
                                }}
                            />
                        </ProCard>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<UnorderedListOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.tasks" />
                                </SectionTitle>
                            }
                            extra={
                                <Space>
                                    {readOnly ? null : (
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={() =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    tasks: [
                                                        ...prev.tasks,
                                                        createTaskItem({ taskId: getNextTaskId(prev.tasks) }),
                                                    ],
                                                }))
                                            }
                                        >
                                            <FormattedMessage id="activity1011Config.actions.addTask" />
                                        </Button>
                                    )}
                                    {sectionSaveExtra('tasks')}
                                </Space>
                            }
                        >
                            <AdminProTable<TaskRow>
                                className="config-table"
                                rowKey="rowKey"
                                search={false}
                                options={false}
                                pagination={false}
                                cardProps={{ bodyStyle: { padding: 0 } }}
                                dataSource={taskDataSource}
                                columns={taskColumns}
                                scroll={{ x: 760 }}
                                editable={
                                    readOnly
                                        ? undefined
                                        : {
                                              type: 'single',
                                              onSave: async (_key, row) => {
                                                  setConfig((prev) => ({
                                                      ...prev,
                                                      tasks: prev.tasks.map((item, index) => {
                                                          const rowKey = `task-${item.taskId || index}-${index}`;
                                                          if (rowKey !== row.rowKey) return item;
                                                          return {
                                                              ...item,
                                                              type: String(row.type || '').trim(),
                                                              points: Number(row.points ?? 0),
                                                              linkUrl: String(row.linkUrl || '').trim(),
                                                          };
                                                      }),
                                                  }));
                                              },
                                          }
                                }
                                locale={{
                                    emptyText: formatMessage({ id: 'activity1011Config.empty.tasks' }),
                                }}
                            />
                        </ProCard>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<CalendarOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.checkin" />
                                </SectionTitle>
                            }
                            extra={sectionSaveExtra('checkin')}
                        >
                            <div className="checkin-grid">
                                {Array.from({ length: ACTIVITY_1011_CHECKIN_DAYS }).map((_, index) => (
                                    <div className="checkin-day" key={index}>
                                        <span className="checkin-day__label">
                                            {formatMessage(
                                                { id: 'activity1011Config.fields.checkinDay' },
                                                { day: index + 1 },
                                            )}
                                        </span>
                                        <InputNumber
                                            min={0}
                                            size="large"
                                            style={{ width: '100%' }}
                                            disabled={readOnly}
                                            value={checkinPoints[index] ?? 0}
                                            onChange={(value) => {
                                                setConfig((prev) => {
                                                    const next = [
                                                        ...(prev.checkinDailyPoints?.length
                                                            ? prev.checkinDailyPoints
                                                            : defaultCheckinDailyPoints()),
                                                    ];
                                                    next[index] = Number(value ?? 0);
                                                    return { ...prev, checkinDailyPoints: next };
                                                });
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </ProCard>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<RiseOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.spread" />
                                </SectionTitle>
                            }
                            extra={sectionSaveExtra('spread')}
                        >
                            <div className="spread-platforms">
                                {platforms.map((platform) => (
                                    <div className="platform-block" key={platform}>
                                        <div className="platform-block__title">
                                            {formatMessage({
                                                id:
                                                    PLATFORM_LABEL_IDS[platform] ||
                                                    'activity1011Config.platforms.other',
                                            })}
                                        </div>
                                        <div className="spread-grid">
                                            {ACTIVITY_1011_METRIC_KEYS.map((metric, ruleIndex) => {
                                                const rule = metricRules[platform]?.[metric] || {
                                                    points: 0,
                                                    threshold: 0,
                                                };
                                                const metricName = formatMessage({
                                                    id:
                                                        METRIC_LABEL_IDS[metric] ||
                                                        'activity1011Config.metrics.play',
                                                });
                                                const updateRule = (patch: {
                                                    points?: number;
                                                    threshold?: number;
                                                }) => {
                                                    setConfig((prev) => {
                                                        const nextRules = ensurePlatformMetrics(
                                                            prev.shareMetricsPointsRules || {},
                                                            platforms,
                                                        );
                                                        nextRules[platform] = {
                                                            ...(nextRules[platform] || {}),
                                                            [metric]: {
                                                                points:
                                                                    patch.points ??
                                                                    nextRules[platform]?.[metric]?.points ??
                                                                    0,
                                                                threshold:
                                                                    patch.threshold ??
                                                                    nextRules[platform]?.[metric]?.threshold ??
                                                                    0,
                                                            },
                                                        };
                                                        return {
                                                            ...prev,
                                                            shareMetricsPointsRules: nextRules,
                                                        };
                                                    });
                                                };
                                                return (
                                                    <div className="metric-tile" key={`${platform}-${metric}`}>
                                                        <span className="metric-tile__label">
                                                            {formatMessage(
                                                                {
                                                                    id: 'activity1011Config.fields.ruleTitle',
                                                                },
                                                                {
                                                                    index: ruleIndex + 1,
                                                                    threshold: rule.threshold || 0,
                                                                    metric: metricName,
                                                                },
                                                            )}
                                                        </span>
                                                        <div className="metric-tile__row">
                                                            <span>
                                                                <FormattedMessage id="activity1011Config.fields.ruleEvery" />
                                                            </span>
                                                            <InputNumber
                                                                min={0}
                                                                className="metric-tile__input"
                                                                disabled={readOnly}
                                                                value={rule.threshold}
                                                                onChange={(value) =>
                                                                    updateRule({
                                                                        threshold: Number(value ?? 0),
                                                                    })
                                                                }
                                                            />
                                                            <span>
                                                                <FormattedMessage id="activity1011Config.fields.ruleTimes" />
                                                            </span>
                                                            <span className="metric-tile__plus">+</span>
                                                            <InputNumber
                                                                min={0}
                                                                className="metric-tile__input"
                                                                disabled={readOnly}
                                                                value={rule.points}
                                                                onChange={(value) =>
                                                                    updateRule({ points: Number(value ?? 0) })
                                                                }
                                                            />
                                                            <span>
                                                                <FormattedMessage id="activity1011Config.fields.rulePoints" />
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ProCard>

                        <ProCard
                            className="section-card"
                            title={
                                <SectionTitle icon={<GiftOutlined />}>
                                    <FormattedMessage id="activity1011Config.sections.rewards" />
                                </SectionTitle>
                            }
                            extra={
                                <Space>
                                    {readOnly ? null : (
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={() =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    rewardRankTiers: [
                                                        ...prev.rewardRankTiers,
                                                        createRewardRankTier(),
                                                    ],
                                                }))
                                            }
                                        >
                                            <FormattedMessage id="activity1011Config.actions.addTier" />
                                        </Button>
                                    )}
                                    {sectionSaveExtra('rewards')}
                                </Space>
                            }
                        >
                            <AdminProTable<RewardRow>
                                className="config-table"
                                rowKey="rowKey"
                                search={false}
                                options={false}
                                pagination={false}
                                cardProps={{ bodyStyle: { padding: 0 } }}
                                dataSource={rewardDataSource}
                                columns={rewardColumns}
                                editable={
                                    readOnly
                                        ? undefined
                                        : {
                                              type: 'single',
                                              onSave: async (_key, row) => {
                                                  setConfig((prev) => ({
                                                      ...prev,
                                                      rewardRankTiers: prev.rewardRankTiers.map((item, index) => {
                                                          const rowKey = `reward-${item.minRank}-${item.maxRank}-${index}`;
                                                          if (rowKey !== row.rowKey) return item;
                                                          return {
                                                              minRank: Number(row.minRank ?? 1),
                                                              maxRank: Number(row.maxRank ?? 1),
                                                              amount: Number(row.amount ?? 0),
                                                          };
                                                      }),
                                                  }));
                                              },
                                          }
                                }
                                locale={{
                                    emptyText: formatMessage({ id: 'activity1011Config.empty.rewards' }),
                                }}
                            />
                        </ProCard>
                    </Space>
                </div>
            </Spin>
        </Permission>
    );
};

export default PageActivity1011TaskConfig;
